import React, { useEffect, useState } from "react";
import axios from "axios";
import { styled } from "@mui/material/styles";
import MuiAccordion from "@mui/material/Accordion";
import MuiAccordionSummary from "@mui/material/AccordionSummary";
import MuiAccordionDetails from "@mui/material/AccordionDetails";
import { Download } from "@mui/icons-material";
import ArrowForwardIosSharpIcon from "@mui/icons-material/ArrowForwardIosSharp";
import FilePresentIcon from "@mui/icons-material/FilePresent";
import DangerousIcon from "@mui/icons-material/Dangerous";
import DescriptionIcon from "@mui/icons-material/Description";
import BASE_URL from "../../config/apiConfig";
import { generateDownload } from "../../util/FormUtils";
import { showToast, showErrorToast } from "../../util/showToast";

const Accordion = styled((props) => (
    <MuiAccordion disableGutters elevation={0} square {...props}  />
))(({ theme }) => ({
    borderTop: `1px solid ${theme.palette.divider}`,
    '&:first-child': {
        borderTop: 'none',
    },
}));

const AccordionSummary = styled((props) => (
    <MuiAccordionSummary
        expandIcon={<ArrowForwardIosSharpIcon sx={{ fontSize: '0.9rem' }} />}
        {...props}
    />
))(({ theme }) => ({
    backgroundColor: '#def3fc',
    flexDirection: 'row-reverse',
    '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
        transform: 'rotate(90deg)',
    },
    '& .MuiAccordionSummary-content': {
        marginLeft: theme.spacing(1),
    },
}));

const AccordionDetails = styled(MuiAccordionDetails)(() => ({
    padding: 0,
}));

const BoxWorkflowFileManager = ({ epId, pullListFolderId }) => {
    const [maxWorkflowId, setMaxWorkflowId] = useState(0);
    const [pullListFiles, setPullListFiles] = useState([]);
    const [sampleQualityFiles, setSampleQualityFiles] = useState([]);
    const [disposalRequestFiles, setDisposalRequestFiles] = useState([]);
    const [otherFiles, setOtherFiles] = useState([]);
    const [statuses, setStatuses] = useState([]);
    const [shipmentFromRepos, setShipmentFromRepos] = useState([]);

    const fetchFiles = async () => {
        try {
            const countResponse = await axios.get(BASE_URL + `/api/pullLists/count/${epId}`, { withCredentials: true });
            const newMaxWorkflowId = parseInt(countResponse.data.maxPullListId);
            const newPullListFiles = [];
            const newSampleQualityFiles = [];
            const newDisposalRequestFiles = [];
            const newOtherFiles = [];
            const newStatuses = [];
            const newShipmentFromRepos = [];

            const getBoxFileName = async (fileId) => {
                if (!fileId) {
                    return null;
                }

                try {
                    const response = await axios.get(BASE_URL + `/api/box/files/${fileId}`, { withCredentials: true });
                    return response.data.fileName;
                } catch (error) {
                    console.error(`Error fetching Box file ${fileId}:`, error);
                    return null;
                }
            };

            await Promise.all(
                Array.from({ length: newMaxWorkflowId }, (_, index) => index + 1).map(async (workflowId) => {
                    try {
                        const workflowResponse = await axios.get(BASE_URL + `/api/pullLists/ep/${epId}/pl/${workflowId}/latest`, { withCredentials: true });
                        const { pullListFile, sampleQualityFile, disposalRequestFile, boxFilesId, status, shipmentFromRepo } = workflowResponse.data;
                        const [pullListFileName, sampleQualityFileName, disposalRequestFileName] =
                            await Promise.all([getBoxFileName(pullListFile), getBoxFileName(sampleQualityFile), getBoxFileName(disposalRequestFile)]);

                        newPullListFiles[workflowId] = { id: pullListFile, name: pullListFileName };
                        newSampleQualityFiles[workflowId] = { id: sampleQualityFile, name: sampleQualityFileName };
                        newDisposalRequestFiles[workflowId] = { id: disposalRequestFile, name: disposalRequestFileName };
                        newStatuses[workflowId] = status;
                        newShipmentFromRepos[workflowId] = shipmentFromRepo;

                        try {
                            const boxResponse = await axios.get(BASE_URL + `/api/box/folders/${boxFilesId}`, { withCredentials: true });
                            newOtherFiles[workflowId] = boxResponse.data.items.filter((file) => file.id !== disposalRequestFile);
                        } catch (error) {
                            console.error(`Error fetching Box workflow files for workflow ${workflowId}:`, error);
                            newOtherFiles[workflowId] = [];
                        }
                    } catch (error) {
                        console.error(`Error fetching workflow ${workflowId}:`, error);
                        newPullListFiles[workflowId] = null;
                        newSampleQualityFiles[workflowId] = null;
                        newDisposalRequestFiles[workflowId] = null;
                        newOtherFiles[workflowId] = [];
                        newStatuses[workflowId] = null;
                        newShipmentFromRepos[workflowId] = null;
                    }
                })
            );

            setMaxWorkflowId(newMaxWorkflowId);
            setPullListFiles(newPullListFiles);
            setSampleQualityFiles(newSampleQualityFiles);
            setDisposalRequestFiles(newDisposalRequestFiles);
            setOtherFiles(newOtherFiles);
            setStatuses(newStatuses);
            setShipmentFromRepos(newShipmentFromRepos);
        } catch (error) {
            console.error("Error fetching pull list files:", error);
        }
    };

    useEffect(() => {
        fetchFiles();
    }, []);

    const handleDownload = async (fileId) => {
        showToast("Downloading file...", <Download style={{ color: "#2155bc" }} />);

        axios.get(BASE_URL + `/api/box/download/${fileId}`, { responseType: "blob", withCredentials: true })
            .then((response) => { generateDownload(response) })
            .catch((error) => { showErrorToast(`File download failed: ${error}`, <DangerousIcon style={{ color: "#ef4337" }} />) });
    };

    const renderFile = (file) => {
        if (file && file.id && file.name) {
            return (
                <div className="boxFileManagerBodyFile">
                    <a
                        href={`https://bri.app.box.com/file/${file.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="boxFileManagerBodyFileTitle"
                    >
                        <DescriptionIcon fontSize="small" />
                        {file.name}
                    </a>

                    <div className="boxFileManagerBodyFileActions">
                        <button onClick={() => handleDownload(file.id)}>
                            <Download />
                        </button>
                    </div>
                </div>
            );
        }
    };

    const renderOtherFiles = (files) => {
        if (files && files.length > 0) {
            return files.map((file) => (
                <div
                    key={file.id}
                    className="boxFileManagerBodyFile"
                >
                    <a
                        href={`https://bri.app.box.com/file/${file.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="boxFileManagerBodyFileTitle"
                    >
                        <DescriptionIcon fontSize="small" />
                        {file.name}
                    </a>

                    <div className="boxFileManagerBodyFileActions">
                        <button onClick={() => handleDownload(file.id)}>
                            <Download />
                        </button>
                    </div>
                </div>
            ));
        }
    };

    return (
        <div id="boxFileManager" className="boxFileManager">
            <div className="boxFileManagerHeader">
                <a
                    href={`https://bri.app.box.com/folder/${pullListFolderId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="boxFileManagerHeaderTitle"
                >
                    {`EP${epId} Workflow Files`}
                </a>
            </div>

            <div className="boxFileManagerBody">
                {maxWorkflowId <= 0 ? (
                    <div className="boxFileManagerBodyNone">
                        <FilePresentIcon fontSize="small" />
                        <p>No files found.</p>
                    </div>
                ) : (
                    //for (workflowId = 1; workflowId <= maxWorkflowId; workflowId++)
                    Array.from({ length: maxWorkflowId }, (_, i) => i + 1).map((workflowId) => (
                        <Accordion
                            key={workflowId}
                            className="boxFileManagerWorkflow"
                        >
                            <AccordionSummary className="boxFileManagerWorkflowHeader">
                                {`Series ${workflowId} - ${shipmentFromRepos[workflowId] === 'Yes' ? 'Shipment From Repo' : shipmentFromRepos[workflowId] === 'No' ? 'Real Time' : 'Sample Disposal'} - ${statuses[workflowId]}`}
                            </AccordionSummary>
                            <AccordionDetails className="boxFileManagerWorkflowBody">
                                {pullListFiles[workflowId] && <>{renderFile(pullListFiles[workflowId])}</>}
                                {sampleQualityFiles[workflowId] && <>{renderFile(sampleQualityFiles[workflowId])}</>}
                                {disposalRequestFiles[workflowId] && <>{renderFile(disposalRequestFiles[workflowId])}</>}
                                {otherFiles[workflowId] && <>{renderOtherFiles(otherFiles[workflowId])}</>}
                            </AccordionDetails>
                        </Accordion>
                    ))
                )}
            </div>
        </div>
    );
};

export default BoxWorkflowFileManager;